import { Animation } from './Animation';
import { SettingsProperty } from '../properties/SettingsProperty';
import { InterpolatedProperty } from '../properties/InterpolatedProperty';
import { Vector3 } from '../types/Vector3';
import { Quaternion } from '../types/Quaternion';
import { Color } from '../types/Color';

export class BoundsAnimation<T> extends Animation {
    private _property: SettingsProperty<T> | null = null;
    private _animateComponent: string | null = null;
    
    // Animation parameters
    private _speed: number = 1.0;
    private _easing: 'linear' | 'easeInOut' | 'easeIn' | 'easeOut' = 'linear';
    private _direction: 'forward' | 'backward' | 'pingpong' = 'pingpong';
    private _currentDirection: 'forward' | 'backward' = 'forward';
    
    // Randomization parameters
    private _useRandomization: boolean = false;
    private _randomSpeedVariation: number = 0.3; // 30% variation
    private _randomAmplitudeVariation: number = 0.2; // 20% variation
    private _randomPhaseOffset: number = 0;
    private _randomSpeedMultiplier: number = 1.0;
    private _randomAmplitudeMultiplier: number = 1.0;
    
    // Cycle tracking
    private _cycleCount: number = 0;
    private _lastValue: number = 0;
    private _valueRange: { min: number; max: number } = { min: 0, max: 1 };

    constructor(name: string = 'Bounds Animation') {
        super(name, 'bounds');
        this.enableRandomization();
    }

    /**
     * Enable randomization for more natural animation
     */
    public enableRandomization(): void {
        this._useRandomization = true;
        this._randomSpeedMultiplier = 1.0 + (Math.random() - 0.5) * this._randomSpeedVariation;
        this._randomAmplitudeMultiplier = 1.0 + (Math.random() - 0.5) * this._randomAmplitudeVariation;
        this._randomPhaseOffset = Math.random() * Math.PI * 2;
    }

    /**
     * Disable randomization for predictable animation
     */
    public disableRandomization(): void {
        this._useRandomization = false;
        this._randomSpeedMultiplier = 1.0;
        this._randomAmplitudeMultiplier = 1.0;
        this._randomPhaseOffset = 0;
    }

    /**
     * Set the property to animate
     */
    public setProperty(property: SettingsProperty<T>): void {
        this._property = property;
        this._updateValueRange();
    }

    /**
     * Set which component to animate (for Vector3, Quaternion, Color)
     */
    public setAnimateComponent(component: string): void {
        this._animateComponent = component;
    }

    /**
     * Set animation speed (cycles per second)
     */
    public setSpeed(speed: number): void {
        this._speed = Math.max(0.1, speed);
    }

    /**
     * Set easing type
     */
    public setEasing(easing: 'linear' | 'easeInOut' | 'easeIn' | 'easeOut'): void {
        this._easing = easing;
    }

    /**
     * Set animation direction
     */
    public setDirection(direction: 'forward' | 'backward' | 'pingpong'): void {
        this._direction = direction;
    }

    /**
     * Update value range based on property bounds
     */
    private _updateValueRange(): void {
        if (!this._property) return;

        if (this._property instanceof InterpolatedProperty) {
            const interpolatedProp = this._property as InterpolatedProperty<any>;
            this._valueRange.min = interpolatedProp.boundsMin ?? 0;
            this._valueRange.max = interpolatedProp.boundsMax ?? 1;
        } else {
            // For non-interpolated properties, use reasonable defaults
            this._valueRange.min = 0;
            this._valueRange.max = 1;
        }
    }

    /**
     * Calculate animated value
     */
    private _calculateAnimatedValue(elapsedTime: number): T | null {
        if (!this._property) return null;

        const effectiveSpeed = this._speed * this._randomSpeedMultiplier;
        const time = elapsedTime * effectiveSpeed + this._randomPhaseOffset;
        
        // Calculate normalized value (0 to 1)
        let normalizedValue = 0;
        
        if (this._direction === 'pingpong') {
            // Ping-pong between 0 and 1
            const cycle = Math.sin(time * Math.PI);
            normalizedValue = Math.abs(cycle);
        } else {
            // Forward or backward
            const cycle = (time % (2 * Math.PI)) / (2 * Math.PI);
            normalizedValue = this._direction === 'forward' ? cycle : 1 - cycle;
        }

        // Apply easing
        normalizedValue = this._applyEasing(normalizedValue);

        // Apply randomization to amplitude
        if (this._useRandomization) {
            normalizedValue *= this._randomAmplitudeMultiplier;
            normalizedValue = Math.max(0, Math.min(1, normalizedValue));
        }

        // Convert to actual value based on property type and range
        return this._convertToPropertyValue(normalizedValue);
    }

    /**
     * Apply easing function
     */
    private _applyEasing(t: number): number {
        switch (this._easing) {
            case 'easeIn':
                return t * t;
            case 'easeOut':
                return 1 - (1 - t) * (1 - t);
            case 'easeInOut':
                return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
            case 'linear':
            default:
                return t;
        }
    }

    /**
     * Convert normalized value to property value
     */
    private _convertToPropertyValue(normalizedValue: number): T | null {
        if (!this._property) return null;

        const currentValue = this._property.value;
        if (!currentValue) return null;

        const { min, max } = this._valueRange;
        const range = max - min;
        const targetValue = min + normalizedValue * range;

        // Handle different property types
        if (this._animateComponent && currentValue instanceof Vector3) {
            const vector = currentValue.clone();
            (vector as any)[this._animateComponent] = targetValue;
            return vector as T;
        } else if (this._animateComponent && currentValue instanceof Quaternion) {
            const quat = currentValue.clone();
            (quat as any)[this._animateComponent] = targetValue;
            return quat as T;
        } else if (this._animateComponent && currentValue instanceof Color) {
            const color = currentValue.clone();
            (color as any)[this._animateComponent] = targetValue;
            return color as T;
        } else if (typeof currentValue === 'number') {
            return targetValue as T;
        }

        return null;
    }

    /**
     * Main animation logic
     */
    protected animate(elapsedTime: number, deltaTime: number): void {
        if (!this._property) return;

        const newValue = this._calculateAnimatedValue(elapsedTime);
        if (newValue !== null) {
            this._property.setImmediate(newValue);
        }
    }

    /**
     * Animation never completes (continuous)
     */
    protected shouldCompleteLoop(elapsedTime: number): boolean {
        return false;
    }

    /**
     * Reset animation state
     */
    protected onReset(): void {
        this._cycleCount = 0;
        this._lastValue = 0;
        this._currentDirection = 'forward';
        
        if (this._useRandomization) {
            this.enableRandomization();
        }
    }

    /**
     * Get current animation progress (0 to 1)
     */
    public getProgress(): number {
        if (!this.isRunning()) return 0;
        
        const elapsedTime = this.getElapsedTimeSeconds();
        const effectiveSpeed = this._speed * this._randomSpeedMultiplier;
        const time = elapsedTime * effectiveSpeed + this._randomPhaseOffset;
        
        if (this._direction === 'pingpong') {
            return Math.abs(Math.sin(time * Math.PI));
        } else {
            const cycle = (time % (2 * Math.PI)) / (2 * Math.PI);
            return this._direction === 'forward' ? cycle : 1 - cycle;
        }
    }

    /**
     * Get animation info for debugging
     */
    public getAnimationInfo(): any {
        return {
            isRunning: this.isRunning(),
            property: this._property?.name || 'None',
            component: this._animateComponent || 'Whole value',
            speed: this._speed,
            easing: this._easing,
            direction: this._direction,
            valueRange: this._valueRange,
            progress: this.getProgress(),
            randomization: {
                enabled: this._useRandomization,
                speedMultiplier: this._randomSpeedMultiplier,
                amplitudeMultiplier: this._randomAmplitudeMultiplier,
                phaseOffset: this._randomPhaseOffset
            }
        };
    }
} 